import {
  LayerProps,
  DataDrivenPropertyValueSpecification,
  FilterSpecification,
  ExpressionSpecification,
} from 'shared/map-exports';
import { FONT_NAMES } from 'features/mapping/styles/constants';
import { FILL_PATTERN_LAYER_MIN_ZOOM } from 'features/mapping/mapping.constants';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import {
  LABEL_LAYER_MIN_ZOOM,
  FEATURE_UID_V2_FIELD_NAME,
  ZONES_SOURCE_LAYER,
  LABELS_SOURCE_LAYER,
} from './constants';
import {
  EVAC_ZONE_LAYER_COLORS,
  EVAC_ZONE_LAYER_PATTERNS,
  EVAC_ZONE_STROKE_COLOR,
  EvacZoneStyles,
  ORDERS_PATTERN,
} from '../../../../../constants';

export const getFillStyleAll = (): LayerProps => {
  return {
    type: 'fill',
    'source-layer': ZONES_SOURCE_LAYER,
    paint: {
      'fill-opacity': 0,
    },
  };
};

export const getLayerFilter = (zoneUIds: string[]): FilterSpecification => {
  return ['in', ['get', FEATURE_UID_V2_FIELD_NAME], ['literal', zoneUIds]];
};

export const getUidFillStyle = (input: {
  color: string;
  zoneIds: string[];
  opacity?: number;
  sourceLayer: string;
}): LayerProps => {
  const { color, zoneIds, opacity = 0.25, sourceLayer } = input;
  return {
    type: 'fill',
    'source-layer': sourceLayer,
    paint: {
      'fill-color': color,
      'fill-opacity': opacity,
    },
    filter: getLayerFilter(zoneIds),
  };
};

export const getEditUidPatternStyle = (
  pattern: string,
  zoneIds: string[],
  sourceLayer: string,
  layerIdPrefix: string,
): LayerProps => {
  const id = `${layerIdPrefix}-${pattern}`;
  // overriden for higher contrast with the red exclamation marks
  const opacity = pattern === ORDERS_PATTERN ? 1 : 0.75;

  return {
    id,
    type: 'fill',
    minzoom: FILL_PATTERN_LAYER_MIN_ZOOM,
    'source-layer': sourceLayer,
    paint: {
      'fill-pattern': pattern,
      'fill-opacity': opacity,
    },
    filter: getLayerFilter(zoneIds),
  };
};

export const lineWidthLightBaseLayer: DataDrivenPropertyValueSpecification<number> =
  ['interpolate', ['linear'], ['zoom'], 6, 0.2, 8, 0.7, 18, 2.0];

export const lineWidthDarkBaseLayer: DataDrivenPropertyValueSpecification<number> =
  ['interpolate', ['linear'], ['zoom'], 6, 1, 9, 2.0, 18, 4.0];

export const getStrokeStyleAll = (isDarkBaseLayer: boolean): LayerProps => {
  return {
    type: 'line',
    'source-layer': ZONES_SOURCE_LAYER,
    paint: {
      'line-color': EVAC_ZONE_STROKE_COLOR,
      'line-width': isDarkBaseLayer
        ? lineWidthDarkBaseLayer
        : lineWidthLightBaseLayer,
    },
  };
};

export const getSelectedZonePatternStyle = (
  selectedZoneId: string,
  sourceLayer = ZONES_SOURCE_LAYER,
  idKey = FEATURE_UID_V2_FIELD_NAME,
): LayerProps => {
  return getSelectedPatternStyle({
    idKey,
    otherLayerProps: {
      'source-layer': sourceLayer,
    },
    selectedId: selectedZoneId,
  });
};

export const getSelectedZoneStrokeStyle = (
  selectedZoneId: string,
  sourceLayer = ZONES_SOURCE_LAYER,
  idKey = FEATURE_UID_V2_FIELD_NAME,
): LayerProps => {
  return getSelectedStrokeStyle({
    idKey,
    otherLayerProps: {
      'source-layer': sourceLayer,
    },
    selectedId: selectedZoneId,
  });
};

export const getEditFillStyle = (
  evacZoneUIds: string[],
  sourceLayer = ZONES_SOURCE_LAYER,
): LayerProps => ({
  type: 'fill',
  'source-layer': sourceLayer,
  paint: {
    'fill-color': ['coalesce', ['feature-state', 'color'], 'transparent'],
    'fill-opacity': 0.25,
  },
  filter: getLayerFilter(evacZoneUIds),
});

export const getEditStrokeStyle = (
  isDarkBaseLayer: boolean,
  evacZoneUIds: string[],
  sourceLayer: string,
): LayerProps => ({
  type: 'line',
  'source-layer': sourceLayer,
  paint: {
    'line-color': EVAC_ZONE_STROKE_COLOR,
    'line-width': isDarkBaseLayer
      ? lineWidthDarkBaseLayer
      : lineWidthLightBaseLayer,
  },
  filter: getLayerFilter(evacZoneUIds),
});

export const getLabelStyleAll = (): LayerProps => ({
  type: 'symbol',
  'source-layer': LABELS_SOURCE_LAYER,
  minzoom: LABEL_LAYER_MIN_ZOOM,
  paint: {
    'text-color': '#000000',
    'text-halo-width': 1.8,
    'text-halo-color': '#ffffff',
    'text-opacity': 1,
  },
  layout: {
    'text-field': ['get', 'display_name'],
    'text-anchor': 'center',
    'text-justify': 'auto',
    'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 13, 16],
    'text-font': FONT_NAMES.bold,
    'text-allow-overlap': ['step', ['zoom'], false, 13, true],
  },
});

export const getLabelTextField = (
  nameOverridesByUid: Record<string, string> = {},
): DataDrivenPropertyValueSpecification<string> => {
  const overrides = Object.entries(nameOverridesByUid);
  if (overrides.length === 0) {
    return ['get', 'display_name'];
  }
  return [
    'match',
    ['get', FEATURE_UID_V2_FIELD_NAME],
    ...overrides.flat(),
    ['get', 'display_name'],
  ] as ExpressionSpecification;
};

export const getEditLabelStyle = (
  evacZoneUIds: string[],
  sourceLayer: string,
  nameOverridesByUid?: Record<string, string>,
): LayerProps => ({
  type: 'symbol',
  'source-layer': sourceLayer,
  minzoom: LABEL_LAYER_MIN_ZOOM,
  paint: {
    'text-color': '#000000',
    'text-halo-width': 1.8,
    'text-halo-color': '#ffffff',
    'text-opacity': 1,
  },
  layout: {
    'text-field': getLabelTextField(nameOverridesByUid),
    'text-anchor': 'center',
    'text-justify': 'auto',
    'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 13, 16],
    'text-font': FONT_NAMES.bold,
    'text-allow-overlap': ['step', ['zoom'], false, 13, true],
  },
  filter: getLayerFilter(evacZoneUIds),
});

const createEvacZoneMatchExpression = (
  styleMap: Record<string, Record<string, string>>,
  fallbackValue: string,
): DataDrivenPropertyValueSpecification<string> => {
  const matchExpression: ExpressionSpecification = [
    'match',
    // Create a unique key by concatenating evac_zone_style and status
    ['concat', ['get', 'evac_zone_style'], '-', ['get', 'status']],
  ];

  Object.entries(styleMap).forEach(([zoneStyle, statusValues]) => {
    Object.entries(statusValues).forEach(([status, value]) => {
      matchExpression.push(`${zoneStyle}-${status}`, value);
    });
  });

  matchExpression.push(fallbackValue);

  return matchExpression;
};

const getStatusFilter = (regionIds?: number[]): FilterSpecification => {
  if (regionIds?.length) {
    return [
      'any',
      ['has', 'status'],
      ['in', ['get', 'region_id'], ['literal', regionIds]],
    ];
  }
  return ['has', 'status'];
};

export const getFillStyle = (input: {
  regionIds: number[];
  opacity?: number;
}): LayerProps => {
  const { regionIds, opacity = 0.25 } = input;
  return {
    type: 'fill',
    'source-layer': ZONES_SOURCE_LAYER,
    paint: {
      'fill-color': createEvacZoneMatchExpression(
        EVAC_ZONE_LAYER_COLORS,
        'transparent',
      ),
      'fill-opacity': opacity,
    },
    filter: getStatusFilter(regionIds),
  };
};

const createPatternOpacityMatchExpression =
  (): DataDrivenPropertyValueSpecification<number> => {
    return [
      'match',
      ['concat', ['get', 'evac_zone_style'], '-', ['get', 'status']],
      ['literal', `${EvacZoneStyles.reverseColors}-orders`],
      0.75,
      1,
    ];
  };

export const getPatternStyle = (): LayerProps => {
  return {
    type: 'fill',
    minzoom: FILL_PATTERN_LAYER_MIN_ZOOM,
    'source-layer': ZONES_SOURCE_LAYER,
    paint: {
      'fill-pattern': createEvacZoneMatchExpression(
        EVAC_ZONE_LAYER_PATTERNS,
        ORDERS_PATTERN,
      ),
      'fill-opacity': createPatternOpacityMatchExpression(),
    },
    filter: getStatusFilter(),
  };
};

export const getStrokeStyle = (input: {
  isDarkBaseLayer: boolean;
  regionIds: number[];
}): LayerProps => {
  const { isDarkBaseLayer, regionIds } = input;
  const baseStyle: LayerProps = {
    type: 'line',
    'source-layer': ZONES_SOURCE_LAYER,
    paint: {
      'line-color': EVAC_ZONE_STROKE_COLOR,
      'line-width': isDarkBaseLayer
        ? lineWidthDarkBaseLayer
        : lineWidthLightBaseLayer,
    },
    filter: getStatusFilter(regionIds),
  };
  return baseStyle;
};

export const getLabelLayerStyle = (regionIds: number[]): LayerProps => ({
  type: 'symbol',
  'source-layer': LABELS_SOURCE_LAYER,
  minzoom: LABEL_LAYER_MIN_ZOOM,
  paint: {
    'text-color': '#000000',
    'text-halo-width': 1.8,
    'text-halo-color': '#ffffff',
    'text-opacity': 1,
  },
  layout: {
    'text-field': ['get', 'display_name'],
    'text-anchor': 'center',
    'text-justify': 'auto',
    'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 13, 16],
    'text-font': FONT_NAMES.bold,
    'text-allow-overlap': ['step', ['zoom'], false, 13, true],
  },
  filter: getStatusFilter(regionIds),
});
